import { useState, useEffect } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { StatusPill, STATUS_FLOW, STATUS_META } from "@/lib/status";
import { PushBell } from "@/components/PushBell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Package, CalendarPlus, CalendarClock, X, RotateCcw, WifiOff } from "lucide-react";
import { OpenInAdmin } from "@/components/OpenInAdmin";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { reorderItems } from "@/lib/reorder";

export default function TrackOrder() {
  const { addItem, setDrawerOpen } = useCart();
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [payingBalance, setPayingBalance] = useState(false);
  const [offlineAt, setOfflineAt] = useState(null);
  const CACHE_KEY = "awg_last_tracked";
  useEffect(() => {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (c?.order) { setOrder(c.order); setOrderNumber(c.order.order_number || ""); setEmail(c.email || ""); if (!navigator.onLine) setOfflineAt(c.at); }
    } catch {}
  }, []);
  const payBalance = async () => {
    setPayingBalance(true);
    try {
      const { data } = await api.post("/payments/pay-balance", { order_id: order.order_id, email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not start payment"); setPayingBalance(false); }
  };

  const minDate = (() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().slice(0, 10); })();

  const search = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setOrder(null); setOfflineAt(null);
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: orderNumber, email } });
      setOrder(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ order: data, email, at: new Date().toISOString() })); } catch {}
    } catch (err) {
      const cached = (() => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; } })();
      if (!err.response && cached?.order && cached.order.order_number?.toUpperCase() === orderNumber.trim().toUpperCase()) {
        setOrder(cached.order); setOfflineAt(cached.at);
      } else {
        setError(apiError(err.response?.data?.detail) || (err.response ? "Order not found" : "No connection — and no saved copy of this order yet"));
      }
    } finally { setLoading(false); }
  };

  const reschedule = async () => {
    if (!newDate) { toast.error("Pick a new date first"); return; }
    setRescheduling(true);
    try {
      const { data } = await api.post("/orders/reschedule", { order_number: order.order_number, email, new_date: newDate });
      setOrder(data); setNewDate("");
      toast.success("New date submitted — we'll review and confirm it shortly");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not submit new date");
    } finally { setRescheduling(false); }
  };

  const currentIdx = order ? STATUS_FLOW.indexOf(order.status) : -1;

  const reorder = () => reorderItems(order.items, addItem, setDrawerOpen);
  const reorderOne = (it) => reorderItems([it], addItem, setDrawerOpen);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Track your order</h1>
      <p className="text-zinc-400 mt-2">Enter your order number and the email used at checkout.</p>

      <form onSubmit={search} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-8 items-end bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
        <div><Label className="text-zinc-300">Order number</Label>
          <Input data-testid="track-order-number" required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="PH-XXXXXX" className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13] font-mono-p" /></div>
        <div><Label className="text-zinc-300">Email</Label>
          <Input data-testid="track-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13]" /></div>
        <Button data-testid="track-submit" disabled={loading} className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-10 px-5 font-semibold"><Search size={16} className="mr-1" />Track</Button>
      </form>

      {error && <p className="text-red-400 mt-4 text-sm font-mono-p" data-testid="track-error">{error}</p>}

      {order && (
        <div className="mt-8 bg-[#12141a] border border-zinc-800 rounded-2xl p-6 animate-fade-up" data-testid="track-result">
          {offlineAt && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#ffd600]/40 bg-[#ffd600]/10 px-3 py-2 text-xs text-[#ffd600] font-mono-p" data-testid="track-offline-banner">
              <WifiOff size={14} /> Offline — showing status saved {new Date(offlineAt).toLocaleString()}
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-zinc-500 font-mono-p uppercase tracking-wider">Order</p>
              <p className="font-display text-2xl font-bold text-white font-mono-p">{order.order_number}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {order.status !== "cancelled" && order.status !== "shipped" && <PushBell email={email} orderNumber={order.order_number} />}
              <OpenInAdmin orderNumber={order.order_number} size="sm" />
              <StatusPill status={order.status} />
            </div>
          </div>

          {order.status === "cancelled" && (
            <div className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="track-cancelled-panel">
              <p className="text-sm font-semibold text-red-400 flex items-center gap-2"><X size={16} />This order was cancelled</p>
              {order.cancel_reason && <p className="text-xs text-zinc-300 mt-1" data-testid="track-cancel-reason">Reason: {order.cancel_reason}</p>}
              <p className="text-xs text-zinc-400 mt-1">If you already sent a deposit or have any questions, please reach out and we'll help right away.</p>
              <Button onClick={reorder} data-testid="track-reorder-btn" className="mt-3 bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-9 px-4 text-sm font-semibold"><RotateCcw size={15} className="mr-1" />Reorder these items</Button>
            </div>
          )}

          {order.status !== "cancelled" && (<>
          <div className="flex items-center mt-8 mb-2">
            {STATUS_FLOW.map((s, i) => (
              <div key={s} className="flex-1 flex items-center last:flex-none">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i <= currentIdx ? "bg-[#ff6a13] text-white" : "bg-zinc-800 text-zinc-600"}`}>{i + 1}</div>
                {i < STATUS_FLOW.length - 1 && <div className={`flex-1 h-0.5 ${i < currentIdx ? "bg-[#ff6a13]" : "bg-zinc-800"}`} />}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-1 text-center">
            {STATUS_FLOW.map((s) => <p key={s} className="text-[10px] text-zinc-500 font-mono-p">{STATUS_META[s].label}</p>)}
          </div>
          </>)}

          {order.delivery_status && (
            <p className="mt-6 text-sm text-zinc-300 flex items-center gap-2 flex-wrap" data-testid="track-delivery"><Package size={16} className="text-[#10b981]" />Delivery: <span className="font-mono-p text-white">{ ({ collected: "Order Collected", delivered: "Order Delivered", closed: "Order Closed" })[order.delivery_status] || order.delivery_status }</span>{order.delivery_timestamps?.[order.delivery_status] && <span className="text-zinc-500 font-mono-p">· {new Date(order.delivery_timestamps[order.delivery_status]).toLocaleDateString()}</span>}</p>
          )}
          {order.status !== "cancelled" && order.fulfillment_date && (
            <p className="mt-3 text-sm text-[#22c3d6] flex items-center gap-2 flex-wrap" data-testid="track-fulfillment"><Package size={16} />Fulfillment date: <span className="font-mono-p text-white">{order.fulfillment_date}</span>
              {(() => {
                const fs = order.fulfillment_status || "pending_approval";
                const meta = { pending_approval: ["Pending approval", "text-[#ffd600] border-[#ffd600]/40 bg-[#ffd600]/10"], approved: ["Confirmed", "text-[#10b981] border-[#10b981]/40 bg-[#10b981]/10"], rejected: ["Needs new date", "text-[#ff007a] border-[#ff007a]/40 bg-[#ff007a]/10"] }[fs];
                return <span className={`text-[11px] font-mono-p px-2 py-0.5 rounded-full border ${meta[1]}`} data-testid="track-date-status">{meta[0]}</span>;
              })()}
            </p>
          )}
          {order.status !== "cancelled" && order.fulfillment_date && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_id}/calendar.ics?token=${order.access_token || ""}`} data-testid="track-ics-link"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-transparent text-white hover:bg-zinc-900 px-4 h-9 text-sm font-semibold">
              <CalendarPlus size={15} /> Add to Calendar
            </a>
          )}

          {order.fulfillment_status === "rejected" && (
            <div className="mt-5 rounded-xl border border-[#ff007a]/40 bg-[#ff007a]/10 p-4" data-testid="reschedule-panel">
              <p className="text-sm font-semibold text-white flex items-center gap-2"><CalendarClock size={16} className="text-[#ff007a]" />We couldn't confirm your requested date</p>
              <p className="text-xs text-zinc-300 mt-1">Please propose a new fulfillment date (at least 2 days out) and we'll review it.</p>
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <Input data-testid="reschedule-date-input" type="date" min={minDate} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13] sm:max-w-[220px]" />
                <Button data-testid="reschedule-submit" onClick={reschedule} disabled={rescheduling} className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-10 px-5 font-semibold">{rescheduling ? "Submitting…" : "Propose new date"}</Button>
              </div>
            </div>
          )}

          {Array.isArray(order.date_history) && order.date_history.length > 0 && (
            <div className="mt-4" data-testid="track-date-history">
              <p className="text-xs text-zinc-500 font-mono-p uppercase tracking-wider mb-1">Date history</p>
              <div className="space-y-0.5">
                {order.date_history.map((h, i) => (
                  <p key={`${h.at}-${i}`} className="text-xs text-zinc-400 font-mono-p">
                    <span className="text-zinc-300">{h.action}</span> · {h.by}
                    {h.from_date ? ` · ${h.from_date}→${h.to_date || "—"}` : (h.to_date ? ` · ${h.to_date}` : "")}
                    <span className="text-zinc-600"> · {new Date(h.at).toLocaleDateString()}</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {order.status !== "cancelled" && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-xs text-zinc-500 font-mono-p uppercase tracking-wider">Items</p>
              <Button onClick={reorder} data-testid="track-reorder-active" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-8 px-4 text-sm font-semibold"><RotateCcw size={14} className="mr-1" />Reorder</Button>
            </div>
          )}
          <div className="mt-3 space-y-2">
            {order.items.map((it, i) => (
              <div key={`${it.name}-${i}`} className="flex items-center gap-3 bg-[#1e222d] border border-zinc-800 rounded-xl p-3">
                <div className={`${it.mockup_render_url ? "w-20 h-20" : "w-12 h-12"} checker rounded-lg overflow-hidden flex items-center justify-center shrink-0`}>
                  {(it.mockup_render_url || it.image_url || it.preview_url) && <img src={fileUrl(it.mockup_render_url || it.preview_url || it.image_url)} alt="" className="max-w-full max-h-full object-contain" data-testid={it.mockup_render_url ? `track-mockup-${i}` : undefined} />}
                </div>
                <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{it.name}</p><p className="text-xs text-zinc-500 font-mono-p">Qty {it.quantity}</p></div>
                <span className="text-sm font-mono-p text-zinc-300">${(it.unit_price * it.quantity).toFixed(2)}</span>
                {(it.image_url || it.preview_url) && (
                  <Button size="sm" variant="outline" onClick={() => reorderOne(it)} data-testid={`track-reorder-line-${i}`} className="h-7 rounded-full border-[#22c3d6]/50 bg-transparent text-[#22c3d6] hover:bg-[#22c3d6]/10 text-xs px-2.5 shrink-0"><RotateCcw size={12} className="mr-1" />Reorder</Button>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between border-t border-zinc-800 mt-4 pt-4">
            <span className="text-zinc-400">Total</span><span className="font-bold text-lg font-mono-p text-white">${order.total.toFixed(2)}</span>
          </div>
          {(order.payments?.length > 0 || order.balance_remaining != null || order.requires_deposit) && (() => {
            const paid = order.amount_paid != null ? order.amount_paid : (order.payment_status === "paid" ? order.total : 0);
            const balance = order.balance_remaining != null ? order.balance_remaining : Math.max(0, +(order.total - paid).toFixed(2));
            const settled = balance <= 0.005;
            return (
              <div className="mt-4 rounded-xl border border-zinc-800 bg-[#1e222d] p-4" data-testid="track-payments">
                {(order.payments || []).length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono-p">Payment history</p>
                    {order.payments.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm" data-testid={`track-payment-${i}`}>
                        <span className="text-zinc-400">{String(p.at || "").slice(0, 10)} · {p.method}{p.note ? ` · ${p.note}` : ""}</span>
                        <span className="font-mono-p text-[#10b981]">${(p.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Paid</span><span className="font-mono-p text-[#10b981]" data-testid="track-paid">${paid.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-zinc-400">{settled ? "Balance" : "Balance due"}</span>
                  <span className={`font-mono-p font-bold ${settled ? "text-[#10b981]" : "text-[#ff4f18]"}`} data-testid="track-balance">${balance.toFixed(2)}</span>
                </div>
                {settled
                  ? <p className="text-xs text-[#10b981] mt-2 font-mono-p" data-testid="track-paid-full">Paid in full — thank you!</p>
                  : (<div className="mt-3">
                      <p className="text-xs text-zinc-400 mb-2">An outstanding balance of <span className="text-white font-mono-p">${balance.toFixed(2)}</span> remains.</p>
                      <Button onClick={payBalance} disabled={payingBalance} data-testid="track-pay-balance" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-10 px-5 font-semibold">{payingBalance ? "Redirecting…" : `Pay balance ($${balance.toFixed(2)})`}</Button>
                    </div>)}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
